import React, { useState } from 'react';
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Switch,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { Controller, useFormContext } from 'react-hook-form';
import _ from 'lodash';
import { Plus } from 'phosphor-react';
import {
  IBaixaPresente,
  IEstatutoCampoMeta,
  IEstatutoFormPayload,
  IEstatutoPessoa,
} from '../../hooks/useEstatuto';
import { PessoasField, soDigitos } from './PessoasField';
import { PresentesField, marcados } from './PresentesField';

/** Quem pode ser escolhido num campo 'pessoa': presentes da Baixa ou fundadores presentes. */
export const escolhiveis = (valores: any): { nome: string; cpf: string }[] => {
  if (Array.isArray(valores?.presentes))
    return marcados(valores.presentes as IBaixaPresente[]).filter(p => p.nome);
  return ((valores?.pessoas ?? []) as IEstatutoPessoa[]).filter(p => p.presente && p.nome);
};

interface DynamicFieldProps {
  meta: IEstatutoCampoMeta;
}

const ListInput: React.FC<{
  value: string[];
  onChange: (next: string[]) => void;
}> = ({ value, onChange }) => {
  const [input, setInput] = useState('');

  const handleAdd = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    if (value.includes(trimmed)) {
      setInput('');
      return;
    }
    onChange([...value, trimmed]);
    setInput('');
  };

  const handleRemove = (item: string) => {
    onChange(value.filter(v => v !== item));
  };

  return (
    <Box>
      <InputGroup>
        <Input
          placeholder="Digite e pressione Enter para adicionar"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
        />
        <InputRightElement>
          <IconButton
            aria-label="Adicionar"
            size="sm"
            variant="ghost"
            icon={<Plus size={16} weight="bold" />}
            onClick={handleAdd}
          />
        </InputRightElement>
      </InputGroup>

      {value.length > 0 && (
        <Flex wrap="wrap" gap={2} mt={3}>
          {value.map(item => (
            <Tag
              key={item}
              size="md"
              bg="blue.50"
              color="blue.700"
              borderRadius="full"
              px={3}
              py={1}
            >
              <TagLabel>{item}</TagLabel>
              <TagCloseButton onClick={() => handleRemove(item)} />
            </Tag>
          ))}
        </Flex>
      )}
    </Box>
  );
};

export const DynamicField: React.FC<DynamicFieldProps> = ({ meta }) => {
  const {
    control,
    getValues,
    formState: { errors },
  } = useFormContext<IEstatutoFormPayload>();

  const error = _.get(errors, meta.name);
  const errMsg = (error?.message ?? '') as string;

  return (
    <FormControl isRequired={meta.required} isInvalid={!!error} mb={5}>
      <FormLabel fontSize="sm" fontWeight="600" color="gray.700" htmlFor={meta.name}>
        {meta.label}
      </FormLabel>

      <Controller
        control={control}
        name={meta.name as any}
        render={({ field }) => {
          const { value, onChange } = field;

          if (meta.type === 'text') {
            return (
              <Input
                id={meta.name}
                value={(value as string) ?? ''}
                onChange={e => onChange(e.target.value)}
                placeholder={meta.help_text ?? undefined}
              />
            );
          }

          if (meta.type === 'textarea') {
            return (
              <Textarea
                id={meta.name}
                value={(value as string) ?? ''}
                onChange={e => onChange(e.target.value)}
                placeholder={meta.help_text ?? undefined}
                minH="120px"
              />
            );
          }

          if (meta.type === 'hora') {
            return (
              <Input
                id={meta.name}
                type="time"
                value={(value as string) ?? ''}
                onChange={e => onChange(e.target.value)}
              />
            );
          }

          if (meta.type === 'presentes') {
            return (
              <PresentesField
                value={Array.isArray(value) ? (value as IBaixaPresente[]) : []}
                onChange={onChange}
              />
            );
          }

          if (meta.type === 'date') {
            return (
              <Input
                id={meta.name}
                type="date"
                value={(value as string) ?? ''}
                onChange={e => onChange(e.target.value)}
              />
            );
          }

          if (meta.type === 'integer') {
            return (
              <Input
                id={meta.name}
                type="number"
                value={value === null || value === undefined ? '' : (value as number)}
                onChange={e => {
                  const v = e.target.value;
                  onChange(v === '' ? null : Number(v));
                }}
                min={meta.min ?? undefined}
                max={meta.max ?? undefined}
              />
            );
          }

          if (meta.type === 'select') {
            return (
              <Select
                id={meta.name}
                value={(value as string) ?? ''}
                onChange={e => onChange(e.target.value)}
                // Com padrão, não deixa voltar ao vazio (o back recusa '').
                placeholder={meta.default == null ? 'Selecione uma opção' : undefined}
              >
                {(meta.options ?? []).map(opt => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </Select>
            );
          }

          if (meta.type === 'boolean') {
            return (
              <Flex
                align="center"
                bg="gray.50"
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                px={3}
                py={2}
              >
                <Text fontSize="sm" color="gray.700" flex={1}>
                  {value ? 'Sim' : 'Não'}
                </Text>
                <Switch
                  id={meta.name}
                  size="md"
                  colorScheme="blue"
                  isChecked={!!value}
                  onChange={e => onChange(e.target.checked)}
                />
              </Flex>
            );
          }

          if (meta.type === 'list') {
            return (
              <ListInput
                value={Array.isArray(value) ? (value as string[]) : []}
                onChange={onChange}
              />
            );
          }

          if (meta.type === 'pessoas') {
            return (
              <PessoasField
                value={Array.isArray(value) ? (value as IEstatutoPessoa[]) : []}
                onChange={onChange}
                cargos={meta.options ?? []}
              />
            );
          }

          if (meta.type === 'pessoa') {
            return (
              <Select
                id={meta.name}
                value={(value as string) ?? ''}
                onChange={e => onChange(e.target.value)}
                placeholder="Selecione entre os presentes"
              >
                {escolhiveis(getValues()).map(p => (
                  <option key={p.cpf} value={soDigitos(p.cpf)}>
                    {p.nome}
                  </option>
                ))}
              </Select>
            );
          }

          return (
            <Input
              id={meta.name}
              value={String(value ?? '')}
              onChange={e => onChange(e.target.value)}
            />
          );
        }}
      />

      {meta.help_text && !error && (
        <Text fontSize="11px" color="gray.500" mt={1}>
          {meta.help_text}
        </Text>
      )}
      {error && <FormErrorMessage>{errMsg}</FormErrorMessage>}
    </FormControl>
  );
};
